import useSWR, { mutate, SWRConfiguration } from "swr";
import { useCallback, useMemo } from "react";

import axios, { fetcher, endpoints } from "src/utils/axios";

import { ISportProviderCard } from "src/types/sportprovider";
import { IPhase } from "src/types/phase";
import { IBlockingSetting } from "src/types/blocking";
import { ITraining } from "src/types/training";
import {
  IPassItem,
  ITicketItem,
  IUserPassItem,
  IUserTicketItem,
} from "src/types/passticket";
import { TransactionItem } from "src/sections/calendar/calendar-view-booking";
import { StreamStatus } from "src/types/livestreaming-types";
import {
  ProviderPlayerDetail,
  ProviderPlayerDirectoryParams,
  ProviderPlayerDirectoryResponse,
  ProviderPlayerHistoryResponse,
  ProviderPlayerHistoryType,
} from "src/types/provider-player";

// ----------------------------------------------------------------------

export function useGetProviders(type: string | null | undefined) {
  const URL = type
    ? endpoints.provider.list + "/" + type
    : endpoints.provider.list;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      providers: (data as Partial<ISportProviderCard>[]) || [],
      providersLoading: isLoading,
      providersError: error,
      providersValidating: isValidating,
      providersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetUserProviderPublicData(providerUrlTag: string) {
  const shouldFetch = providerUrlTag ?? ""; // Only fetch if transactionId is valid
  const URL = shouldFetch
    ? `${endpoints.provider.details}/${providerUrlTag}`
    : null;

  const { data, isLoading, error, isValidating } = useSWR(
    shouldFetch ? URL : null,
    fetcher
  );

  const memoizedValue = useMemo(
    () => ({
      provider: data || null,
      providerLoading: isLoading,
      providerError: error,
      providerValidating: isValidating,
      providerEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetUserProviderProfiles(enabled = true) {
  const URL = enabled ? endpoints.auth.providerProfiles : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      providers: data || [],
      providersLoading: isLoading,
      providersError: error,
      providersValidating: isValidating,
      refreshProviderProfiles: mutate,
      providersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetProviderProfileDataGeneral(providerId: number) {
  const URL = endpoints.provider.profile.general + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      provider: data || null,
      providerLoading: isLoading,
      providerError: error,
      providerValidating: isValidating,
      providerEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderProfileDataBilling(providerId: number) {
  const URL = endpoints.provider.profile.billing + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      provider: data || null,
      providerLoading: isLoading,
      providerError: error,
      providerValidating: isValidating,
      providerEmpty: !isLoading && !data,
    }),
    [data?.provider, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderProfileDataSports(providerId: number) {
  const URL = endpoints.provider.profile.sports + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      provider: data || null,
      providerLoading: isLoading,
      providerError: error,
      providerValidating: isValidating,
      providerEmpty: !isLoading && !data,
    }),
    [data?.provider, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderProfileDataVenues(providerId: number) {
  const URL = endpoints.provider.profile.getvenues + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      provider: data || null,
      providerLoading: isLoading,
      providerError: error,
      providerValidating: isValidating,
      providerEmpty: !isLoading && !data,
    }),
    [data?.provider, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderProfileDataImages(providerId: number) {
  const URL = endpoints.provider.profile.images + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      providerImages: data ?? null,
      providerImagesLoading: isLoading,
      providerImagesError: error,
      providerImagesValidating: isValidating,
      providerImagesEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderProfileDataGalleryImages(providerId: number) {
  const URL = endpoints.provider.profile.galleryimages + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      galleryImages: data ?? [],
      galleryImagesLoading: isLoading,
      galleryImagesError: error,
      galleryImagesValidating: isValidating,
      galleryImagesEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderUnreadMessageNr(providerId: number) {
  const URL = endpoints.provider.settings.messages.number + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      messageData: data || {},
      messageNumberLoading: isLoading,
      messageNumberError: error,
      messageNumberValidating: isValidating,
      messageNumberEmpty: !isLoading && !data?.length,
    }),
    [data?.messageData, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderPhases(providerId?: number | null) {
  const URL = providerId
    ? endpoints.provider.settings.phases + "/" + providerId
    : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      phases: data || null,
      phasesLoading: isLoading,
      phasesError: error,
      phasesValidating: isValidating,
      phasesEmpty: !isLoading && !data?.length,
    }),
    [data?.phases, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderTrainings(providerId?: number | null) {
  const URL = providerId
    ? endpoints.provider.settings.trainings + "/" + providerId
    : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      trainings: data || null,
      trainingsLoading: isLoading,
      trainingsError: error,
      trainingsValidating: isValidating,
      trainingsEmpty: !isLoading && !data?.length,
    }),
    [data?.trainings, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderTrainingByEventId(eventId: number) {
  const URL = `${endpoints.provider.settings.events.training}/${eventId}`;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      training: data ?? null, // keep it object-shaped (or null)
      trainingLoading: isLoading,
      trainingError: error,
      trainingValidating: isValidating,
      trainingEmpty: !isLoading && !data, // object presence check
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

// export function useGetProviderTrainingByEventId(eventId: number) {
//   const URL = endpoints.provider.settings.events.training + "/" + eventId;

//   const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
//   const memoizedValue = useMemo(
//     () => ({
//       training: data ?? null,
//       trainingLoading: isLoading,
//       trainingError: error,
//       trainingValidating: isValidating,
//       trainingEmpty: !isLoading && !data?.length,
//     }),
//     [data, error, isLoading, isValidating]
//   );

//   return memoizedValue;
// }

export async function updatePhase(data: Partial<IPhase>, providerId: number) {
  const response = await axios.post(
    endpoints.provider.settings.phase + "/" + providerId,
    data
  );
  await Promise.allSettled([
    mutate(endpoints.provider.settings.phase + "/" + response.data.phase.id),
    mutate(endpoints.provider.settings.phases + "/" + providerId),
  ]);
  return response;
}

export async function deletePhase(providerId: number, phaseId: number) {
  return await axios.delete(
    endpoints.provider.settings.phase + "/" + providerId + "/" + phaseId
  );
}

export function useGetProviderSettingsGlobals(providerId: number) {
  const URL = endpoints.provider.settings.globals + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      globalsettings: data || null,
      globalsettingsLoading: isLoading,
      globalsettingsError: error,
      globalsettingsValidating: isValidating,
      globalsettingsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateProviderSettingsGlobals(
  data: any,
  providerId: number
) {
  const url = endpoints.provider.settings.globals + "/" + providerId;
  const response = await axios.post(url, data);
  await mutate(url);
  await mutate(`/providers/${providerId}/player-requirements`);
  return response;
}

export function useGetProviderPhase(phaseId: number) {
  const URL = endpoints.provider.settings.phase + "/" + phaseId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      phase: data || null,
      phaseLoading: isLoading,
      phaseError: error,
      phaseValidating: isValidating,
      phaseEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderBlockings(providerId: number) {
  const URL = endpoints.provider.settings.blockings + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      blockings: data || null,
      blockingsLoading: isLoading,
      blockingsError: error,
      blockingsValidating: isValidating,
      blockingsEmpty: !isLoading && !data?.length,
    }),
    [data?.blockings, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateBlockingSetting(
  data: Partial<IBlockingSetting>,
  providerId: number
) {
  return await axios.post(
    endpoints.provider.settings.blockings + "/" + providerId,
    data
  );
}

export async function deleteBlocking(providerId: number, blockingId: number) {
  return await axios.delete(
    endpoints.provider.settings.blockings + "/" + providerId + "/" + blockingId
  );
}

export async function fetchTransactionItemsByReservationId(
  reservationId: number,
  providerId: number
): Promise<TransactionItem[]> {
  const URL =
    endpoints.transaction.item.itembyreservation +
    "/" +
    reservationId +
    "/" +
    providerId;

  const response = await axios.get<TransactionItem[]>(URL);
  return response.data;
}

export async function updateTraining(
  data: Partial<ITraining>,
  providerId: number
) {
  return await axios.post(
    endpoints.provider.settings.training + "/" + providerId,
    data
  );
}

export function useGetProviderTransactions(providerId: number, limit: number) {
  const URL =
    endpoints.provider.transactions.fulldata + "/" + providerId + "/" + limit;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      transactions: data || [],
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderPlayers(providerId: number) {
  const URL = endpoints.provider.players.list + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      players: data || [],
      playersLoading: isLoading,
      playersError: error,
      playersValidating: isValidating,
      playersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderPlayerDirectory(
  providerId: number,
  params: ProviderPlayerDirectoryParams
) {
  const URL = useMemo(() => {
    if (!providerId) return null;

    const searchParams = new URLSearchParams({
      page: String(params.page),
      page_size: String(params.pageSize),
      sort_by: params.sortBy || "name",
      sort_order: params.sortOrder || "asc",
    });

    if (params.query) searchParams.set("q", params.query);
    if (params.membershipState && params.membershipState !== "all") {
      searchParams.set("membership_state", params.membershipState);
    }
    params.membershipTypes?.forEach((membershipType) => {
      searchParams.append("membership_type", membershipType);
    });
    if (params.source && params.source !== "all") {
      searchParams.set("source", params.source);
    }
    if (params.attendance && params.attendance !== "all") {
      searchParams.set("attendance", params.attendance);
    }

    return `${
      endpoints.provider.players.directory
    }/${providerId}?${searchParams.toString()}`;
  }, [
    params.attendance,
    params.membershipState,
    params.membershipTypes,
    params.page,
    params.pageSize,
    params.query,
    params.sortBy,
    params.sortOrder,
    params.source,
    providerId,
  ]);

  const { data, isLoading, error, isValidating, mutate } =
    useSWR<ProviderPlayerDirectoryResponse>(URL, fetcher);

  return useMemo(
    () => ({
      playerDirectory: data,
      players: data?.items || [],
      pagination: data?.pagination,
      playersLoading: isLoading,
      playersError: error,
      playersValidating: isValidating,
      refetchPlayers: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

export function useGetProviderPlayerDetail(
  providerId: number,
  playerId?: number | null,
  enabled = true
) {
  const URL =
    enabled && providerId > 0 && Number(playerId) > 0
      ? `${endpoints.provider.players.detail}/${providerId}/${playerId}`
      : null;
  const { data, isLoading, error, isValidating, mutate } =
    useSWR<ProviderPlayerDetail>(URL, fetcher);

  return useMemo(
    () => ({
      playerDetail: data,
      playerDetailLoading: isLoading,
      playerDetailError: error,
      playerDetailValidating: isValidating,
      refetchPlayerDetail: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );
}

export function useGetProviderPlayerHistory(
  providerId: number,
  playerId: number,
  historyType: ProviderPlayerHistoryType,
  page: number,
  pageSize: number,
  enabled = true
) {
  const URL =
    enabled && providerId > 0 && playerId > 0
      ? `${endpoints.provider.players.history}/${providerId}/${playerId}/${historyType}?page=${page}&page_size=${pageSize}`
      : null;
  const { data, isLoading, error, isValidating } =
    useSWR<ProviderPlayerHistoryResponse>(URL, fetcher);

  return useMemo(
    () => ({
      playerHistory: data,
      playerHistoryLoading: isLoading,
      playerHistoryError: error,
      playerHistoryValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );
}

export async function updatePlayer(data: any, providerId: number) {
  return await axios.post(
    endpoints.provider.players.settings + "/" + providerId + "/" + data.id,
    data
  );
}

export async function sendPlayerRequest(data: any, providerId: number) {
  return await axios.post(
    endpoints.provider.players.request + "/" + providerId,
    data
  );
}

export function useGetProviderTraining(trainingId: number) {
  const URL = endpoints.provider.settings.training + "/" + trainingId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      training: data || [],
      trainingLoading: isLoading,
      trainingError: error,
      trainingValidating: isValidating,
      trainingEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function deleteTraining(providerId: number, trainingId: number) {
  return await axios.delete(
    endpoints.provider.settings.training + "/" + providerId + "/" + trainingId
  );
}

export function useGetProviderTotalTransactions(providerId: number) {
  const URL = endpoints.provider.transactions.count + "/" + providerId;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      transactions: data || [],
      transactionsLoading: isLoading,
      transactionsError: error,
      transactionsValidating: isValidating,
      transactionsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderActivePlayers(providerId: number) {
  const URL = endpoints.provider.players.count + "/" + providerId;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      players: data || [],
      playersLoading: isLoading,
      playersError: error,
      playersValidating: isValidating,
      playersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderTopActivePlayers(providerId: number) {
  const URL = endpoints.provider.players.top + "/" + providerId;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, {
    revalidateOnFocus: true, // Fetches data again when the window regains focus
    refreshInterval: 0, // Disable periodic polling
  });
  const memoizedValue = useMemo(
    () => ({
      topPlayers: data || [],
      topPlayersLoading: isLoading,
      topPlayersError: error,
      topPlayersValidating: isValidating,
      topPlayersEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function addProviderAdminBlocking(providerId: number, data: any) {
  return await axios.post(
    endpoints.provider.calendar.blocking + "/" + providerId,
    data
  );
}

export async function createAdminReservationForPlayer(
  providerId: number,
  data: any
) {
  return await axios.post(
    endpoints.provider.calendar.reservation + "/" + providerId,
    data
  );
}

export function useGetProviderTrainingOccasionData(slotId: string) {
  const URL = endpoints.provider.calendar.training + "/" + slotId;
  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    {
      revalidateOnFocus: true, // Fetches data again when the window regains focus
      refreshInterval: 0, // Disable periodic polling
    }
  );
  const memoizedValue = useMemo(
    () => ({
      training: data || [],
      trainingLoading: isLoading,
      trainingError: error,
      trainingValidating: isValidating,
      trainingEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderPass(passId: number) {
  const URL = endpoints.provider.settings.pass + "/" + passId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      pass: data || null,
      passLoading: isLoading,
      passError: error,
      passValidating: isValidating,
      passEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updatePass(data: Partial<IPassItem>, providerId: number) {
  return await axios.post(
    endpoints.provider.settings.createpass + "/" + providerId,
    data
  );
}

export function useGetProviderPasses(providerId?: number | null) {
  const URL = providerId
    ? endpoints.provider.settings.passes + "/" + providerId
    : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      passes: data || [],
      passesLoading: isLoading,
      passesError: error,
      passesValidating: isValidating,
      passesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderPurchasedPasses(
  providerId: number,
  passId: number
) {
  const URL =
    endpoints.provider.settings.purchasedpasses +
    "/" +
    providerId +
    "/" +
    passId;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      purchasedpasses: data || [],
      purchasedpassesLoading: isLoading,
      purchasedpassesError: error,
      purchasedpassesValidating: isValidating,
      purchasedpassesEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

type UseGetProviderUserPassesOptions = SWRConfiguration & {
  enabled?: boolean; // default: true only if both ids are valid (>0)
};

export function useGetProviderUserPasses(
  providerId?: number,
  userId?: number,
  options: UseGetProviderUserPassesOptions = {}
) {
  const { enabled, ...swrConfig } = options;

  const hasValidIds = Number(providerId) > 0 && Number(userId) > 0;
  const shouldFetch = enabled ?? hasValidIds;

  const URL = shouldFetch
    ? `${endpoints.provider.settings.userpasses}/${providerId}/${userId}`
    : null;

  const { data, isLoading, error, isValidating, mutate } = useSWR(
    URL,
    fetcher,
    swrConfig
  );

  const refetch = useCallback(() => {
    if (!shouldFetch) return Promise.resolve(undefined);
    return mutate();
  }, [shouldFetch, mutate]);

  const memoizedValue = useMemo(
    () => ({
      purchasedpasses: data || [],
      purchasedpassesLoading: shouldFetch ? isLoading : false,
      purchasedpassesError: shouldFetch ? error : undefined,
      purchasedpassesValidating: shouldFetch ? isValidating : false,
      purchasedpassesEmpty: shouldFetch ? !isLoading && !data?.length : false,
      refetch,
    }),
    [data, error, isLoading, isValidating, refetch, shouldFetch]
  );

  return memoizedValue;
}

// export function useGetProviderUserPasses(providerId: number, userId: number) {
//   const URL =
//     endpoints.provider.settings.userpasses + "/" + providerId + "/" + userId;

//   const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
//   const memoizedValue = useMemo(
//     () => ({
//       purchasedpasses: data || [],
//       purchasedpassesLoading: isLoading,
//       purchasedpassesError: error,
//       purchasedpassesValidating: isValidating,
//       purchasedpassesEmpty: !isLoading && !data?.length,
//       refetch: mutate,
//     }),
//     [data, error, isLoading, isValidating, mutate]
//   );

//   return memoizedValue;
// }

export function useGetProviderPublicPasses(providerId: number) {
  const URL = endpoints.provider.public.passes + "/" + providerId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      passes: data || [],
      passesLoading: isLoading,
      passesError: error,
      passesValidating: isValidating,
      passesEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateUserPass(
  data: Partial<IUserPassItem>,
  providerId: number,
  userPassId: number
) {
  return await axios.post(
    endpoints.provider.settings.userpass + "/" + providerId + "/" + userPassId,
    data
  );
}

export async function providerAddUserPass(
  data: Partial<IUserPassItem>,
  providerId: number
) {
  return await axios.post(
    endpoints.provider.settings.adduserpass + "/" + providerId,
    data
  );
}

export async function providerAddUserTicket(data: any, providerId: number) {
  return await axios.post(
    endpoints.provider.settings.adduserticket + "/" + providerId,
    data
  );
}

export async function subtractCreditUserPass(
  userPassId: number,
  providerId: number,
  amount: number,
  type: string
) {
  return await axios.post(
    endpoints.provider.settings.subtractuserpass +
      "/" +
      providerId +
      "/" +
      userPassId,
    { amount, type }
  );
}

export async function deletePass(providerId: number, passId: number) {
  return await axios.delete(
    endpoints.provider.settings.pass + "/" + providerId + "/" + passId
  );
}

//TICKET ENDPOINTS
export function useGetProviderTicket(ticketId: number) {
  const URL = endpoints.provider.settings.ticket + "/" + ticketId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      ticket: data || null,
      ticketLoading: isLoading,
      ticketError: error,
      ticketValidating: isValidating,
      ticketEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetProviderPurchasedTickets(
  providerId: number,
  ticketId: number
) {
  const URL =
    endpoints.provider.settings.purchasedtickets +
    "/" +
    providerId +
    "/" +
    ticketId;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      purchasedtickets: data || [],
      purchasedticketsLoading: isLoading,
      purchasedticketsError: error,
      purchasedticketsValidating: isValidating,
      purchasedticketsEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetProviderUserTickets(providerId: number, userId: number) {
  const URL =
    endpoints.provider.settings.usertickets + "/" + providerId + "/" + userId;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      purchasedtickets: data || [],
      purchasedticketsLoading: isLoading,
      purchasedticketsError: error,
      purchasedticketsValidating: isValidating,
      purchasedticketsEmpty: !isLoading && !data?.length,
      refetch: mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetProviderPublicTickets(ticketId: number) {
  const URL = endpoints.provider.public.tickets + "/" + ticketId;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      tickets: data || null,
      ticketsLoading: isLoading,
      ticketsError: error,
      ticketsValidating: isValidating,
      ticketsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function updateTicket(
  data: Partial<ITicketItem>,
  providerId: number
) {
  return await axios.post(
    endpoints.provider.settings.ticket + "/" + providerId,
    data
  );
}

export function useGetProviderTickets(providerId?: number | null) {
  const URL = providerId
    ? endpoints.provider.settings.tickets + "/" + providerId
    : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      tickets: data || [],
      ticketsLoading: isLoading,
      ticketsError: error,
      ticketsValidating: isValidating,
      ticketsEmpty: !isLoading && !data?.length,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function deleteTicket(providerId: number, ticketId: number) {
  return await axios.delete(
    endpoints.provider.settings.tickets + "/" + providerId + "/" + ticketId
  );
}

export async function updateUserTicket(
  data: Partial<IUserTicketItem>,
  providerId: number,
  userTicketId: number
) {
  return await axios.post(
    endpoints.provider.settings.userticket +
      "/" +
      providerId +
      "/" +
      userTicketId,
    data
  );
}

export async function prepareTestBarionPaymentApi() {
  return await axios.post(endpoints.provider.barion.testprepare);
}

export async function checkSzamlazzhuConfig(providerId: number) {
  return await axios.post(
    endpoints.invoice.checkSzamlazzhuConfig + "/" + providerId
  );
}

export async function checkBillingoConfig(providerId: number) {
  return await axios.post(
    endpoints.invoice.checkBillingoConfig + "/" + providerId
  );
}

export async function updateReservationBlocking(data: any, providerId: number) {
  return await axios.put(
    endpoints.provider.calendar.reservation + "/" + providerId,
    data
  );
}

export async function createAdminTrainingForPlayer(
  providerId: number,
  data: any
) {
  return await axios.post(
    endpoints.provider.calendar.training + "/" + providerId,
    data
  );
}

export async function createAdminTrainingForDummyPlayer(
  providerId: number,
  data: any
) {
  return await axios.post(
    endpoints.provider.calendar.dummytraining + "/" + providerId,
    data
  );
}

// src/api/providerCustomColumnImport.ts

export async function previewCustomColumnImport(
  providerId: number,
  file: File
) {
  const form = new FormData();
  form.append("file", file);

  return await axios.post(
    endpoints.provider.custom_columns.import.preview + "/" + providerId,
    form
  );
}

export async function commitCustomColumnImport(providerId: number, file: File) {
  const form = new FormData();
  form.append("file", file);

  return await axios.post(
    endpoints.provider.custom_columns.import.commit + "/" + providerId,
    form
  );
}

export type MembershipRequestItem = {
  id: number;
  provider_id: number;
  requestor_id: number;
  requestor_type: string;
  user_email: string | null;
  request_date: string;
  response_date: string | null;
  created_at: string;
  modified_at: string;
  status: number;
  data: string | null;
  first_name: string | null;
  last_name: string | null;
  provider_name: string | null;
  is_registered: boolean;
  has_membership: boolean;
};

export function useGetMembershipRequests(providerId: number) {
  const URL = endpoints.provider.membershiprequests.list + "/" + providerId;

  const { data, isLoading, error, isValidating, mutate } = useSWR(URL, fetcher);
  const memoizedValue = useMemo(
    () => ({
      membershiprequests: (data as MembershipRequestItem[]) || [],
      membershiprequestsLoading: isLoading,
      membershiprequestsError: error,
      membershiprequestsValidating: isValidating,
      membershiprequestsEmpty: !isLoading && !data?.length,
      refetchmembershiprequests: mutate,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function deleteMembershipRequest(
  providerId: number,
  requestId: number
) {
  return await axios.delete(
    endpoints.provider.membershiprequests.delete +
      "/" +
      providerId +
      "/" +
      requestId
  );
}

export async function acceptMembershipRequest(
  providerId: number,
  requestId: number
) {
  return await axios.post(
    endpoints.provider.membershiprequests.accept +
      "/" +
      providerId +
      "/" +
      requestId
  );
}

export async function requestMembershipRequest(providerId: number) {
  return await axios.post(
    endpoints.provider.membershiprequests.request + "/" + providerId
  );
}

export function useGetProviderStreams(providerId: number) {
  const URL = providerId
    ? `${endpoints.provider.livestreaming.root}/${providerId}/streams`
    : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      streams: (data || []) as any[], // we’ll map it later to UI Stream[]
      streamsLoading: isLoading,
      streamsError: error,
      streamsValidating: isValidating,
      streamsEmpty: !isLoading && (!data || data.length === 0),
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function createProviderStreamYoutubeLink(
  providerId: number,
  body: any
) {
  const url = `${endpoints.provider.livestreaming.root}/${providerId}/streams/youtube-link`;
  const res = await axios.post(url, body);
  return res.data;
}

export async function createProviderStreamThirdParty(
  providerId: number,
  body: any
) {
  const url = `${endpoints.provider.livestreaming.root}/${providerId}/streams/thirdparty`;
  const res = await axios.post(url, body);
  return res.data;
}

export async function deleteProviderLiveStream(
  providerId: number,
  streamId: number
) {
  return await axios.delete(
    `${endpoints.provider.livestreaming.root}/${providerId}/streams/${streamId}`
  );
}

export async function updateProviderStreamStatus(
  providerId: number,
  streamId: number,
  status: StreamStatus
) {
  const url = `${endpoints.provider.livestreaming.root}/${providerId}/streams/status/${streamId}`;

  const res = await axios.post(url, {
    status,
  });

  return res.data;
}

export async function updateProviderStreamVisibility(
  providerId: number,
  streamId: number,
  visibility: string
) {
  const url = `${endpoints.provider.livestreaming.root}/${providerId}/streams/visibility/${streamId}`;

  const res = await axios.post(url, {
    visibility,
  });

  return res.data;
}

export function useGetProviderSetting(providerId?: number, setting?: string) {
  const URL =
    providerId && setting
      ? `${endpoints.provider.settings.settings.root}/${providerId}/${setting}`
      : null;

  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher);

  const memoizedValue = useMemo(
    () => ({
      setting: data || null,
      settingValue: data?.value ?? null,
      settingExists: !!data?.exists,
      settingLoading: isLoading,
      settingError: error,
      settingValidating: isValidating,
      settingEmpty: !isLoading && !data?.exists,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}
